import {cleanName, startState, offlineNotice, playsOn, SERVER_MESSAGES, NAME_MAX, NAME_BLOCKED} from '../libs/startState.js';
import {listsFromJson, normaliseEmail, MAX_LENGTH} from '../libs/emailCheck.js';
import {escapeHtml} from '../libs/shareLinks.js';
import {UI} from '../libs/palette.js';
import sfx from './MenuSfx';
import showOfflineNotice from './OfflineNotice';

// The address Weel's own footer uses for Privacy (Bosco, 19 Oct 2026). It
// redirects to the legal page that lists the Weel Global Privacy Policy, so
// it stays right when the policy PDF is replaced.
const PRIVACY_URL = 'https://letsweel.com/privacy';
const DOMAINS_URL = 'email-domains.json';
// The bot trap. Its value goes to start_run as `website`, but the field itself
// has a meaningless name: a password manager's identity autofill fills one
// called "website", and that player would be flagged a bot.
export const HONEYPOT_NAME = 'rc-f7q';

const {INK, PAPER, LIME, ERROR, OK} = UI;

// the pixel tick from the approved mockup, lime on a dark green square
const TICK = `<svg class="tick" viewBox="0 0 9 7" aria-hidden="true" shape-rendering="crispEdges"><path fill="${LIME}" d="M7 0h2v2H7zM6 2h2v1H6zM5 3h2v1H5zM0 3h2v1H0zM1 4h2v1H1zM2 5h4v1H2zM4 4h2v1H4zM3 6h2v1H3z"/></svg>`;

/**
 * Styles for the name and email fields, sized to match the start screen's
 * inputs and Start button (same fonts, borders and shadows).
 */
export const START_FORM_STYLES = `
#menu .collect {
  background: ${PAPER}; border: 3px solid ${INK}; box-shadow: 8px 8px 0 ${INK};
  padding: 24px; display: flex; flex-direction: column; min-width: 0;
  container: collect / inline-size;
}
/* name, email and Start share one row so Start is above the fold */
#menu .collect .fields { display: grid; grid-template-columns: 190px minmax(0, 1fr) 190px; column-gap: 22px; }
#menu .cfld { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
#menu .clbl { font-size: 9px; line-height: 1; display: flex; align-items: center; gap: 8px; height: 14px; }
#menu .inwrap { position: relative; display: flex; }
#menu .collect .inwrap input {
  font: inherit; font-size: 10px; letter-spacing: 1px; background: ${PAPER}; color: ${INK};
  border: 3px solid ${INK}; box-shadow: 5px 5px 0 ${INK}; padding: 21px 18px; width: 100%; height: 62px;
  caret-color: transparent; text-transform: none;
}
#menu .collect input.upper { text-transform: uppercase; }
#menu .cfld.em input { letter-spacing: 0; }
#menu .collect input::placeholder { color: ${INK}; opacity: .55; }
#menu .collect input:focus-visible { outline: 3px solid ${INK}; outline-offset: 3px; }
#menu .collect input:focus::placeholder { color: transparent; }
#menu .collect input.good { box-shadow: 5px 5px 0 ${LIME}, 10px 10px 0 ${INK}; }
#menu .collect input.bad { border-color: ${ERROR}; box-shadow: 5px 5px 0 ${ERROR}; }
/* The native caret is a hairline at this size, so a character-sized block is drawn instead. */
#menu .inwrap .caret {
  position: absolute; top: 50%; left: 0; width: 10px; height: 16px; margin-top: -8px;
  background: ${INK}; pointer-events: none; visibility: hidden; animation: caret-blink 1s step-end infinite;
}
#menu .inwrap.typing .caret { visibility: visible; }
#menu .inwrap .measure { position: absolute; visibility: hidden; white-space: pre; font: inherit; font-size: 10px; letter-spacing: 1px; }
@keyframes caret-blink { 50% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { #menu .inwrap .caret { animation: none; } }

#menu .okico { width: 14px; height: 14px; background: ${OK}; display: inline-grid; place-items: center; flex: none; }
#menu .okico[hidden] { display: none; }
#menu .okico .tick { width: 9px; height: auto; display: block; }

#menu .row2 { margin-top: 18px; }
#menu .leftcol { display: flex; flex-direction: column; }
/* fixed two-line slot so no state moves anything */
#menu .st {
  height: 28px; font-size: 8px; line-height: 14px; letter-spacing: 0; display: flex;
  align-items: flex-start; gap: 6px;
}
#menu .st.hint { opacity: .7; }
#menu .st.err { color: ${ERROR}; }
#menu .st.okmsg { color: ${OK}; }
#menu .st .ico {
  flex: none; width: 14px; height: 14px; background: ${ERROR}; color: #fff;
  display: grid; place-items: center; font-size: 9px;
}
#menu .st .okico { width: 14px; height: 14px; }
#menu .consent-row {
  margin-top: 12px; display: flex; align-items: center; gap: 8px; font-size: 9px; line-height: 1.3;
  height: 16px; letter-spacing: 0; cursor: pointer;
}
#menu .consent-row input { appearance: none; -webkit-appearance: none; margin: 0; width: 16px; height: 16px; flex: none;
  border: 2px solid ${INK}; background: ${PAPER}; cursor: pointer; display: grid; place-items: center; }
#menu .consent-row input:checked { background: ${LIME}; }
#menu .consent-row input:checked::after { content: ''; width: 6px; height: 6px; background: ${INK}; }
#menu .consent-row input:focus-visible { outline: 2px solid ${INK}; outline-offset: 3px; }
#menu .cnotice { margin-top: 8px; font-size: 8px; line-height: 15px; letter-spacing: 0; opacity: .7; }
#menu .cnotice a {
  color: ${INK}; display: inline-block; padding: 8px 0; margin: -8px 0;
  text-decoration: underline; text-underline-offset: 3px; text-decoration-thickness: 1px;
}
#menu .startcol { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
#menu .startcol .cap { font-size: 8px; line-height: 14px; height: 14px; white-space: nowrap; letter-spacing: 0; opacity: .75; text-align: right; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
#menu .startcol .cap.go { opacity: 1; }
#menu .startcol .start { width: 100%; height: 62px; padding: 0 16px; }
#menu .startcol .start.busy { font-size: 12px; }
/* The one-row layout needs a wide panel. Narrower, Start drops under the
   fields; narrower still (a phone), it goes back under the consent notice so
   that is read first. Container queries, because the panel's width depends on
   the footer layout as well as the window. */
@container collect (max-width: 669px) {
  #menu .collect .fields { grid-template-columns: minmax(0, 2fr) minmax(0, 3fr); }
  #menu .startcol { grid-column: 1 / -1; margin-top: 18px; align-items: stretch; }
  #menu .startcol .cap { text-align: left; }
}
@container collect (max-width: 520px) {
  #menu .collect .fields { display: contents; }
  #menu .collect .cfld + .cfld { margin-top: 18px; }
  #menu .startcol { order: 1; }
}
#menu .hp { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; }
`;

// the lists are fetched once per page, however many times the menu opens
let listsPromise = null;

/**
 * loadLists
 * Fetches dist/email-domains.json. If it can't be had (offline, blocked) the
 * form falls back to the format check alone and the server, which is the
 * authority, still turns away a free address.
 * @returns {Promise<{blocked: Set<string>, allowed: Set<string>}>}
 */
function loadLists() {
  if (!listsPromise) {
    listsPromise = fetch(DOMAINS_URL)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`email-domains.json ${res.status}`);
        }
        return res.json();
      })
      .then(listsFromJson)
      .catch(() => listsFromJson({}));
  }
  return listsPromise;
}

/**
 * The name and work-email form in the start screen's footer panel.
 * Menu mounts it, tells it which dog is picked, and gets the started run back
 * through onStart.
 */
export default class StartForm {
  /**
   * @param {Object} options
   * @param {{startRun: Function}} options.leaderboard
   * @param {Function} [options.loadLists] replaceable in tests
   * @param {Function} [options.notify] shows the score-not-saved notice
   */
  constructor({leaderboard, loadLists: loader = loadLists, notify = showOfflineNotice}) {
    this.leaderboard = leaderboard;
    this.loadLists = loader;
    this.notify = notify;
    this.lists = null;
    this.dog = null;
    this.dogName = '';
    this.touched = false;
    this.serverMessage = '';
    this.starting = false;
  }

  /**
   * markup
   * @param {{name: string, email: string}} values prefilled from storage
   * @returns {string}
   */
  markup({name, email}) {
    return `
      <section class="collect" aria-label="Your details">
        <div class="fields">
          <label class="cfld nm"><span class="clbl">YOUR NAME<span class="okico" data-for="name" hidden>${TICK}</span></span>
            <span class="inwrap"><input type="text" id="player-name" class="upper" maxlength="${NAME_MAX}"
              placeholder="YOUR NAME" autocomplete="off" value="${escapeHtml(name)}">
              <span class="caret" aria-hidden="true"></span><span class="measure" aria-hidden="true"></span></span></label>
          <label class="cfld em"><span class="clbl">WORK EMAIL<span class="okico" data-for="email" hidden>${TICK}</span></span>
            <span class="inwrap"><input type="text" inputmode="email" id="player-email" placeholder="you@company.com"
              autocomplete="email" autocapitalize="off" spellcheck="false" maxlength="${MAX_LENGTH}" value="${escapeHtml(email)}">
              <span class="caret" aria-hidden="true"></span><span class="measure" aria-hidden="true"></span></span></label>
          <div class="startcol">
            <div class="cap" aria-live="polite"></div>
            <button class="start" type="button" disabled>START ▶</button>
          </div>
        </div>
        <div class="hp" aria-hidden="true"><input type="text" name="${HONEYPOT_NAME}" tabindex="-1" autocomplete="off"
          data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other"></div>
        <div class="row2">
          <div class="leftcol">
            <div class="st hint" role="status"></div>
            <label class="consent-row"><input type="checkbox" id="player-consent"><span>Send me Weel news and product updates</span></label>
            <div class="cnotice">Your name shows on the public leaderboard. Weel keeps your email on file. <a href="${PRIVACY_URL}" target="_blank" rel="noopener noreferrer">Privacy policy.</a></div>
          </div>
        </div>
      </section>
    `;
  }

  /**
   * bind
   * @param {HTMLElement} root the menu root
   * @param {Object} handlers
   * @param {Function} handlers.onStart called with {name, email, consent, token}
   * @param {Function} handlers.onChange called with {name, email} as they change
   */
  bind(root, {onStart, onChange}) {
    this.el = {
      name: root.querySelector('#player-name'),
      email: root.querySelector('#player-email'),
      consent: root.querySelector('#player-consent'),
      hp: root.querySelector(`input[name="${HONEYPOT_NAME}"]`),
      status: root.querySelector('.st'),
      cap: root.querySelector('.startcol .cap'),
      start: root.querySelector('.start'),
      tickName: root.querySelector('[data-for="name"]'),
      tickEmail: root.querySelector('[data-for="email"]')
    };
    this.onStart = onStart;
    this.onChange = onChange;
    const {name, email, start} = this.el;

    bindCaret(name);
    bindCaret(email);

    name.addEventListener('input', (event) => {
      const clean = cleanName(name.value);
      if (clean !== name.value) {
        name.value = clean;
      }
      // the server's word on the name goes once the name changes
      if (this.serverMessage === NAME_BLOCKED) {
        this.serverMessage = '';
      }
      this.changed();
      sfx.key(event);
    });
    email.addEventListener('input', (event) => {
      this.serverMessage = '';
      this.changed();
      sfx.key(event);
    });
    email.addEventListener('blur', () => {
      this.touched = true;
      this.render();
    });
    [name, email].forEach((input) => input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !start.disabled) {
        start.click();
      }
    }));
    start.addEventListener('click', () => this.submit());

    this.render();
    // until the lists arrive the email shows no status and START stays disabled
    this.loadLists().then((lists) => {
      this.lists = lists;
      this.render();
    });
  }

  /**
   * setDog
   * @param {string|null} id the picked dog
   * @param {string} name its display name
   */
  setDog(id, name) {
    this.dog = id;
    this.dogName = String(name || '').toUpperCase();
    this.render();
  }

  /** @private */
  changed() {
    this.render();
    this.onChange({name: this.values().name, email: this.values().email});
  }

  /**
   * values
   * @returns {{name: string, email: string, consent: boolean}}
   */
  values() {
    return {
      name: this.el.name.value.trim(),
      email: normaliseEmail(this.el.email.value),
      consent: this.el.consent.checked
    };
  }

  /** @returns {Object} the derived state for the current inputs */
  state() {
    const {name, email} = this.values();
    return startState({
      name, email, dog: this.dog, dogName: this.dogName, lists: this.lists, touched: this.touched
    });
  }

  /**
   * render
   * Redraws the status line, ticks, caption and button from the current state.
   * @private
   */
  render() {
    const s = this.state();
    const {status, cap, start, email, name, tickName, tickEmail} = this.el;
    const shownStatus = this.serverMessage ? 'bad' : s.status;
    const message = this.serverMessage || s.message;

    status.className = `st ${shownStatus === 'bad' ? 'err' : shownStatus === 'ok' ? 'okmsg' : 'hint'}`;
    status.setAttribute('role', shownStatus === 'bad' ? 'alert' : 'status');
    status.innerHTML = shownStatus === 'bad'
      ? `<span class="ico" aria-hidden="true">!</span><span></span>`
      : shownStatus === 'ok' ? `<span class="okico">${TICK}</span><span></span>` : '<span></span>';
    status.lastElementChild.textContent = message;

    tickName.hidden = !s.nameOk;
    tickEmail.hidden = !s.emailOk;
    // the status line speaks for one field: the name when it is refused, else the email
    const nameBad = message === NAME_BLOCKED;
    const emailBad = shownStatus === 'bad' && !nameBad;
    name.classList.toggle('good', s.nameOk);
    name.classList.toggle('bad', nameBad);
    name.setAttribute('aria-invalid', String(nameBad));
    email.classList.toggle('good', shownStatus === 'ok');
    email.classList.toggle('bad', emailBad);
    email.setAttribute('aria-invalid', String(emailBad));

    cap.textContent = s.caption;
    cap.classList.toggle('go', s.ready);
    start.disabled = !s.ready || this.starting;
  }

  /**
   * submit
   * Re-checks, asks the server to open the run, and hands the result on. The
   * server is the authority on the email, so an email it refuses stays on this
   * screen; any other failure plays on without a token.
   * @private
   */
  async submit() {
    const s = this.state();
    if (!s.ready || this.starting) {
      return;
    }
    const {name, email, consent} = this.values();
    this.starting = true;
    this.el.start.textContent = 'STARTING...';
    this.el.start.classList.add('busy');
    this.render();

    const result = await this.leaderboard.startRun({
      name, dog: this.dog, email, consent, website: this.el.hp.value
    });

    if (playsOn(result)) {
      // no connection, or the rate limit: play on off the board, and say so
      this.notify(offlineNotice(result));
      this.onStart({name, email, consent, token: result.ok ? result.token : null});
      return;
    }
    this.starting = false;
    this.serverMessage = SERVER_MESSAGES[result.code] || SERVER_MESSAGES.invalid_email;
    this.el.start.textContent = 'START ▶';
    this.el.start.classList.remove('busy');
    this.render();
  }
}

/**
 * bindCaret
 * Keeps the block caret on the input's insertion point.
 * @param {HTMLInputElement} input
 */
function bindCaret(input) {
  const field = input.parentNode;
  const caret = field.querySelector('.caret');
  const measure = field.querySelector('.measure');

  const place = () => {
    const focused = document.activeElement === input;
    const collapsed = input.selectionStart === input.selectionEnd;
    field.classList.toggle('typing', focused && collapsed);
    if (!focused || !collapsed) {
      return;
    }
    const style = window.getComputedStyle(input);
    measure.style.letterSpacing = style.letterSpacing;
    measure.style.textTransform = style.textTransform;
    measure.textContent = input.value.slice(0, input.selectionStart);
    const left = parseFloat(style.borderLeftWidth) + parseFloat(style.paddingLeft) +
      measure.offsetWidth - input.scrollLeft;
    caret.style.left = `${Math.round(left)}px`;
    // restart the blink so the caret is solid while it moves
    caret.style.animation = 'none';
    void caret.offsetWidth;
    caret.style.animation = '';
  };

  ['input', 'focus', 'blur', 'keyup', 'click', 'select'].forEach((type) => {
    input.addEventListener(type, place);
  });
  // arrow keys and Home/End move the insertion point without an input event
  input.addEventListener('keydown', () => window.requestAnimationFrame(place));
}
